import {
  AuthError,
  BrowserAuthError,
  BrowserConfigurationAuthError,
  ClientAuthError,
  ClientConfigurationError,
  InteractionRequiredAuthError,
  ServerError,
} from '@azure/msal-browser';

/**
 * Classified outcome for any error surfaced by MSAL.
 *
 * Hubcode's auth model has three user-visible states:
 *   1. Logged in + recognised role               -> view the app (no classifier needed; success path)
 *   2. Logged in + no recognised role            -> /access-denied
 *   3. Login data invalid for any reason         -> redirect to login
 *
 * The classifier translates MSAL failure modes into one of:
 *   - reauth             : login data isn't usable; redirect to login. The
 *                          default. Covers expired tokens, missing accounts,
 *                          network/server failures, security mismatches,
 *                          unknown errors.
 *   - logout_then_reauth : the local MSAL cache (or B2C session state) is
 *                          believed to be stuck in a way that survives a plain
 *                          loginRedirect; clear it via logout first, then
 *                          re-auth. Same end state as `reauth` from the
 *                          user's perspective, just heavier (one extra
 *                          round-trip and no SSO benefit).
 *
 *                          OPT-IN ONLY. MSAL's documented recovery for every
 *                          standard OAuth/OIDC error code is plain interactive
 *                          re-acquisition; MSAL invalidates stale refresh
 *                          tokens on its own. This kind is for the rare cases
 *                          where local evidence shows MSAL doesn't self-correct
 *                          (e.g. cache shape mismatch after a library upgrade,
 *                          B2C policy quirk, or a deliberate "force fresh
 *                          login" requirement). Each code routed here is a
 *                          deviation from MSAL best practice and should be
 *                          documented with the specific failure mode that
 *                          justifies it. See the code-set comments in this
 *                          file for the current entries and their pending
 *                          investigations.
 *   - denied             : an authorization decision was reached and refused
 *                          access; route to /access-denied. Covers IdP-side
 *                          decisions to refuse (OAuth `access_denied`,
 *                          including user-declined consent at the IdP) and
 *                          policy-driven refusals (unauthorized_client,
 *                          invalid_scope). The "logged in with no recognised
 *                          role" case is *not* an MSAL error and is dispatched
 *                          as `denied` by the user-loading layer.
 *   - config_error       : the app itself is misconfigured (wrong client ID,
 *                          broken authority URL, etc). Redirecting to login
 *                          won't help — the same error will fire on every
 *                          retry. Operator must fix; route to a generic error
 *                          page.
 *   - cancelled          : user aborted the flow before a decision was reached.
 *                          MSAL only emits this from popup flows
 *                          (`user_cancelled`). Distinct from `denied` because
 *                          no decision was made.
 *
 *                          NOTE for Hubcode: the app uses redirect-only flows
 *                          (loginRedirect, acquireTokenSilent, logoutRedirect),
 *                          so this branch is currently unreachable in practice.
 *                          "User cancelled at the B2C login/consent screen"
 *                          comes back as a redirect with OAuth error
 *                          `access_denied`, which routes to `denied`, not
 *                          `cancelled`. Kept for API completeness if popup
 *                          auth is ever added.
 */
export type AuthOutcome =
  | { kind: 'reauth';             reason: string; errorCode?: string }
  | { kind: 'logout_then_reauth'; reason: string; errorCode?: string }
  | { kind: 'denied';             reason: string; errorCode?: string }
  | { kind: 'config_error';       reason: string; errorCode?: string }
  | { kind: 'cancelled';          reason: string; errorCode?: string };

// OAuth/OIDC codes (ServerError.errorCode) where the IdP explicitly refused
// access. Map to /access-denied — user authenticated but is not permitted.
const SERVER_DENIED_CODES = new Set<string>([
  'access_denied',        // user declined consent at IdP / CA policy block
  'unauthorized_client',  // app registration not authorized for tenant
  'invalid_scope',        // requested scope not granted
]);

// OAuth/OIDC codes that signal the app itself (not the user's session) is
// misconfigured. Send to operator-facing error page — re-login won't help.
const SERVER_CONFIG_CODES = new Set<string>([
  'invalid_client',         // client_id misconfigured / unknown to AAD
  'invalid_request',        // malformed request structure
  'unsupported_grant_type', // app requesting a flow AAD doesn't allow
]);

// Codes that need a state-clearing logout before re-auth.
//
// OPT-IN ONLY. MSAL.js v4 invalidates stale refresh tokens internally and the
// next interactive flow replaces them via cache write — for most OAuth/OIDC
// error codes, plain `reauth` is the documented recovery and no application-
// level logout is needed. MSAL docs do not recommend "logout then reauth" as
// a general recovery pattern.
//
// Each entry below MUST justify its presence: which observed failure does it
// fix, what evidence shows plain `reauth` is insufficient, and what's the
// follow-up investigation that could potentially remove it. Defensive
// additions ("just in case") get reverted on review.
//
// The current entries are develop-parity holdovers: develop's AppComponent
// switch routed these codes through a full logout. There is no documented
// rationale for that choice on develop — it predates the classifier extraction
// and may simply be defensive coding. They are kept here to preserve existing
// behaviour until each case is investigated and either justified or removed.
const SERVER_LOGOUT_THEN_REAUTH_CODES = new Set<string>([
  // Develop parity. TODO investigate: MSAL.js v4 marks the refresh token stale
  // on invalid_grant and the subsequent interactive flow should replace cached
  // tokens. If that proves true in practice, this entry can move to plain `reauth`
  // (the documented MSAL recovery path) — better UX, no extra logout round-trip.
  'invalid_grant',
]);

const CLIENT_LOGOUT_THEN_REAUTH_CODES = new Set<string>([
  // Develop parity. TODO investigate: this code signals MSAL gave up on silent
  // renewal; the documented recovery is plain interactive re-acquisition.
  // The classifier-level logout is develop's choice, not MSAL's recommendation.
  'token_renewal_error',
]);

// BrowserAuthError code for popup cancellation. Dormant under redirect-only.
const BROWSER_CANCELLED_CODES = new Set<string>([
  'user_cancelled',
]);

/**
 * Map an MSAL or unknown error to a single classified outcome.
 *
 * Never throws. Default is `reauth` — any failure mode that isn't a positive
 * denial or a positively identified config issue routes to login. This matches
 * Hubcode's stated core flow: "if login data isn't valid for any reason,
 * redirect to login."
 *
 * Telemetry: the original code is preserved in `reason` and `errorCode` so
 * operators can still split failure types (e.g. `invalid_grant` vs `network_error`
 * vs `server_error`) in dashboards even though they all route to the same
 * user-facing destination.
 *
 * B2C note: B2C policy errors (AADB2C90xxx) arrive via ServerError.subError,
 * not errorCode. Sub-error handling is intentionally out of scope here —
 * extend with a B2C-specific branch when/if that ticket lands.
 */
export function classifyAuthError(err: unknown): AuthOutcome {
  if (err == null) {
    return { kind: 'reauth', reason: 'no_error_object' };
  }

  // InteractionRequiredAuthError extends ServerError in MSAL v4 — class check
  // first so we catch all current and future interaction-required codes
  // (interaction_required, consent_required, login_required, bad_token,
  // interrupted_user, ux_not_allowed, no_tokens_found, refresh_token_expired,
  // native_account_unavailable, etc.) without a per-code Set to maintain.
  if (err instanceof InteractionRequiredAuthError) {
    const code = err.errorCode || 'interaction_required';
    return { kind: 'reauth', reason: code, errorCode: code };
  }

  if (err instanceof ServerError) {
    const code = (err.errorCode || '').toLowerCase();
    if (SERVER_DENIED_CODES.has(code)) {
      return { kind: 'denied', reason: code, errorCode: code };
    }
    if (SERVER_CONFIG_CODES.has(code)) {
      return { kind: 'config_error', reason: code, errorCode: code };
    }
    if (SERVER_LOGOUT_THEN_REAUTH_CODES.has(code)) {
      return { kind: 'logout_then_reauth', reason: code, errorCode: code };
    }
    // server_error, temporarily_unavailable, login_required, consent_required,
    // interaction_required (server variant), unknown codes — all collapse to reauth.
    return { kind: 'reauth', reason: code || 'unknown_server_error', errorCode: code };
  }

  if (err instanceof ClientConfigurationError || err instanceof BrowserConfigurationAuthError) {
    const code = (err.errorCode || 'client_config_error').toLowerCase();
    // authority_mismatch is structurally a ClientConfigurationError but the
    // user-visible recovery is "clear the bad account and force re-login,"
    // not "operator deploys a fix." Treat as reauth.
    if (code === 'authority_mismatch') {
      return { kind: 'reauth', reason: code, errorCode: code };
    }
    return { kind: 'config_error', reason: code, errorCode: code };
  }

  if (err instanceof BrowserAuthError) {
    const code = (err.errorCode || '').toLowerCase();
    if (BROWSER_CANCELLED_CODES.has(code)) {
      return { kind: 'cancelled', reason: code, errorCode: code };
    }
    // popup/iframe lifecycle, network transport (no_network_connectivity,
    // post_request_failed, timed_out, etc.), and even interaction_in_progress
    // all mean "auth state isn't usable right now" — collapse to reauth.
    // The downstream waitForIdle/inProgress$ pattern is expected to prevent
    // interaction_in_progress from surfacing here; if it does, falling back
    // to reauth is safer than synthesizing a new outcome kind.
    return { kind: 'reauth', reason: code || 'unknown_browser_auth_error', errorCode: code };
  }

  if (err instanceof ClientAuthError) {
    const code = (err.errorCode || '').toLowerCase();
    if (CLIENT_LOGOUT_THEN_REAUTH_CODES.has(code)) {
      return { kind: 'logout_then_reauth', reason: code, errorCode: code };
    }
    return { kind: 'reauth', reason: code || 'unknown_client_auth_error', errorCode: code };
  }

  if (err instanceof AuthError) {
    const code = err.errorCode || 'unknown_auth_error';
    return { kind: 'reauth', reason: code, errorCode: code };
  }

  // Not an MSAL error at all — thrown string, DOMException, RxJS sentinel.
  return { kind: 'reauth', reason: 'non_msal_error' };
}

/**
 * Convenience predicate for call sites that only need to ask
 * "does this require the user to re-authenticate?".
 *
 * Returns true for both `reauth` and `logout_then_reauth` — they share the
 * same user-facing outcome (the user must sign in again), they only differ
 * in whether the action layer clears local state first. Call sites that
 * need to distinguish the two should branch on `kind` directly instead.
 */
export function shouldReauth(err: unknown): boolean {
  const kind = classifyAuthError(err).kind;
  return kind === 'reauth' || kind === 'logout_then_reauth';
}

/** Convenience predicate for "the user actively chose to stop" (vs an error). Dormant under redirect-only flows. */
export function wasCancelled(err: unknown): boolean {
  return classifyAuthError(err).kind === 'cancelled';
}
